import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { axiosCaller } from '../../../utils/apiCaller';
import { toast } from 'react-toastify';
import { STATUS_MAP } from './NewProductionBoard';
import MikaFileManager, { MikaFile } from '../../../components/MikaFileManager/MikaFileManager';
import CommentThread from './CommentThread';

const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
};

const formatTime = (totalSeconds: number): string => {
    const sec = Math.floor(totalSeconds);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

/**
 * Per-material visibility check for simple users. In 'free' flow mode the
 * prerequisite chain is bypassed entirely — workers see every material
 * regardless of upstream state (matches the row-level filter in ProductRow).
 */
export const isMaterialVisible = (
    materialId: number | null,
    temporaryMaterialId: string | null,
    currentResponsibilityId: number,
    allStatuses: any[],
    isAdmin: boolean,
    flowMode: 'linear' | 'free' = 'linear'
): boolean => {
    if (isAdmin) return true;
    if (flowMode === 'free') return true;
    if (temporaryMaterialId) return true;
    if (!materialId) return true;

    const currentIndex = allStatuses.findIndex((s: any) => s.responsibilityId === currentResponsibilityId);
    if (currentIndex <= 0) return true;

    for (let i = currentIndex - 1; i >= 0; i--) {
        const earlierStatus = allStatuses[i];
        const earlierMaterial = (earlierStatus.materialStatuses || []).find(
            (m: any) => m.materialId === materialId
        );
        if (earlierMaterial) {
            return [1, 4].includes(earlierMaterial.responsibilityStatusId);
        }
    }

    const prevStatus = allStatuses[currentIndex - 1];
    return [1, 4, 9].includes(prevStatus.responsibilityStatusId);
};

interface Props {
    product: any;
    status: any;
    isAdmin: boolean;
    userResponsibilityIds: number[];
    flowMode?: 'linear' | 'free';
    onClose: () => void;
    onStatusUpdated: () => void;
    readOnly?: boolean;
    // Deep-link from a notification: when set, the panel auto-selects this
    // material on mount so the user lands directly on the comment they were
    // notified about. Null/undefined = default behaviour (no auto-select).
    initialMaterialId?: number | null;
}

const ExpansionPanel: React.FC<Props> = ({ product, status, isAdmin, userResponsibilityIds, flowMode = 'linear', onClose, onStatusUpdated, readOnly, initialMaterialId }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const MAIN_STATUSES = useMemo(() => [
        { id: 1, label: fm('production.status.completed', 'Përfunduar'), icon: '✓', key: 'completed', adminOnly: false },
        { id: 3, label: fm('production.status.late', 'Me vonesë'), icon: '⚠', key: 'late', adminOnly: false },
        { id: 4, label: fm('production.status.inProcess', 'Në proces'), icon: '▶', key: 'inProcess', adminOnly: false },
        { id: 2, label: fm('production.status.toDo', 'Për tu realizuar'), icon: '↺', key: 'toDo', adminOnly: true },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const PAUSE_STATUSES = useMemo(() => [
        { id: 5, label: fm('production.status.paused', 'Pauzuar') },
        { id: 6, label: fm('production.status.pausedEndOfShift', 'Perfundimi i orarit') },
        { id: 7, label: fm('production.status.otherWork', 'Kalim në punë tjetër') },
        { id: 8, label: fm('production.status.pausedOther', 'Arsye tjetër') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const { hasMaterials, materialStatuses, responsibilityName, responsibilityId } = status;
    const currentStatusInfo = STATUS_MAP[status.responsibilityStatusId] || STATUS_MAP[9];

    // Form
    const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
    const [showPauseOptions, setShowPauseOptions] = useState(false);
    const [lateComment, setLateComment] = useState('');
    const [adminWarning, setAdminWarning] = useState('');
    const [otherComment, setOtherComment] = useState('');
    const [saving, setSaving] = useState(false);
    const kubitEnabled = useSelector((s: any) => !!s?.app?.companyProfile?.kubitEnabled);
    // In auto mode KuBIT subtracts stock when the production status finishes, so the
    // manual "Consume in KuBIT" button is hidden — a manual click would be a
    // redundant (and potentially racing) second fire for the same material.
    const kubitAutoConsume = useSelector((s: any) => !!s?.app?.companyProfile?.kubitAutoConsume);
    const [consumingMaterialId, setConsumingMaterialId] = useState<number | null>(null);

    // Return categories + resolution
    const [returnCategories, setReturnCategories] = useState<any[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [resolutionNotes, setResolutionNotes] = useState('');

    // Material form
    const [selectedMaterial, setSelectedMaterial] = useState<any>(null);

    // One-shot deep-link consumer: when the panel mounts with an
    // initialMaterialId (set by the board's URL handler after clicking a
    // comment notification), pre-select that material so the user lands
    // directly on the comment they were notified about. Empty-deps useEffect
    // so this only runs on mount — manually re-opening the panel later
    // doesn't re-trigger it.
    useEffect(() => {
        if (initialMaterialId == null) return;
        if (!status?.hasMaterials) return;
        const target = (status.materialStatuses || []).find((m: any) => m.materialId === initialMaterialId);
        if (target) setSelectedMaterial(target);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [matStatusId, setMatStatusId] = useState<number | null>(null);
    const [matLateComment, setMatLateComment] = useState('');
    const [matCategoryId, setMatCategoryId] = useState<number | null>(null);
    const [matResolutionNotes, setMatResolutionNotes] = useState('');

    // Temp material
    const [tempName, setTempName] = useState('');
    const [editTempId, setEditTempId] = useState<number | null>(null);
    const [showAddMat, setShowAddMat] = useState(false);
    const [confirmDeleteTemp, setConfirmDeleteTemp] = useState<{ id: number; name: string } | null>(null);
    const [inlineEditId, setInlineEditId] = useState<number | null>(null);
    const [inlineEditName, setInlineEditName] = useState('');

    // Files
    const [files, setFiles] = useState<any[]>([]);
    const [filesLoaded, setFilesLoaded] = useState(false);

    // Loading gate
    const [commentsReady, setCommentsReady] = useState(0);
    const [matCommentsReady, setMatCommentsReady] = useState(0);
    const onCommentLoaded = () => setCommentsReady(prev => prev + 1);
    const onMatCommentLoaded = () => setMatCommentsReady(prev => prev + 1);

    // Admin
    const [showInfo, setShowInfo] = useState(false);
    const [editStart, setEditStart] = useState(product.start ? product.start.split('T')[0] : '');
    const [editEnd, setEditEnd] = useState(product.end ? product.end.split('T')[0] : '');
    const [dateCom, setDateCom] = useState('');

    // Timer
    const [seconds, setSeconds] = useState(0);
    const [paused, setPaused] = useState(true);
    const [timerReady, setTimerReady] = useState(false);
    const tick = useRef<any>(null);

    // Process events (for banner + inline timeline)
    const [processEvents, setProcessEvents] = useState<any[]>([]);
    const [materialEvents, setMaterialEvents] = useState<any[]>([]);
    const [timelineOpen, setTimelineOpen] = useState(status.responsibilityStatusId === 3);
    const [matTimelineOpen, setMatTimelineOpen] = useState(false);

    // All data ready gate
    const expectedComments = isAdmin ? 2 : 1;
    const allReady = timerReady && filesLoaded && commentsReady >= expectedComments;
    const matAllReady = filesLoaded && matCommentsReady >= expectedComments;

    const parseTracker = (t: any) => {
        if (!t) return { s: 0, r: false };
        let total = 0, running = false;
        (t.detailedTracker || []).forEach((d: any) => {
            total += d.duration || 0;
            if (d.pause === null && d.start) { total += (Date.now() - new Date(d.start.replace(/-/g, '/')).getTime()) / 1000; running = true; }
        });
        return { s: total, r: running };
    };

    // Fetch return categories on mount
    useEffect(() => {
        axiosCaller('GET', 'return-categories').then(res => {
            setReturnCategories(res?.data || []);
        }).catch(() => {});
    }, []);

    // Fetch process events (for banner + inline timeline)
    useEffect(() => {
        axiosCaller('GET', `rework-history/${product.productId}/${responsibilityId}`).then(res => {
            setProcessEvents(res?.data?.events || []);
        }).catch(() => {});
    }, [product.productId, responsibilityId]);

    // Fetch material events when drilling into a material
    useEffect(() => {
        if (!selectedMaterial) { setMaterialEvents([]); return; }
        const matId = selectedMaterial.materialId;
        if (!matId) { setMaterialEvents([]); return; }
        axiosCaller('GET', `rework-history/${product.productId}/${responsibilityId}?materialId=${matId}`).then(res => {
            const events = res?.data?.events || [];
            setMaterialEvents(events);
            if (selectedMaterial.responsibilityStatusId === 3 && events.length > 0) setMatTimelineOpen(true);
        }).catch(() => {});
    }, [selectedMaterial, product.productId, responsibilityId]);

    // Process timer
    useEffect(() => {
        axiosCaller('GET', `products/responsibility-and-status?productId=${product.productId}&responsibilityId=${responsibilityId}`).then(res => {
            const t = (res?.data?.data?.trackers || [])[0];
            if (t) { const { s, r } = parseTracker(t); setSeconds(s); setPaused(!r); }
            setTimerReady(true);
        }).catch(() => setTimerReady(true));
        return () => { if (tick.current) clearInterval(tick.current); };
    }, [product.productId, responsibilityId]);

    // Material timer
    useEffect(() => {
        if (!selectedMaterial) return;
        axiosCaller('GET', `clients/materials/statuses?productId=${product.productId}`).then(res => {
            const md = (res?.data?.data || []).find((m: any) => m.materialId === selectedMaterial.materialId && m.responsibilityId === responsibilityId);
            if (md?.trackers) {
                const t = Array.isArray(md.trackers) ? md.trackers[0] : (typeof md.trackers === 'object' ? md.trackers[Object.keys(md.trackers)[0]] : null);
                if (t) { const { s, r } = parseTracker(t); setSeconds(s); setPaused(!r); return; }
            }
            setSeconds(0); setPaused(true);
        }).catch(() => { setSeconds(0); setPaused(true); });
    }, [selectedMaterial]);

    // Restore process timer on back
    const prevMat = useRef<any>(null);
    useEffect(() => {
        if (prevMat.current && !selectedMaterial) {
            axiosCaller('GET', `products/responsibility-and-status?productId=${product.productId}&responsibilityId=${responsibilityId}`).then(res => {
                const t = (res?.data?.data?.trackers || [])[0];
                if (t) { const { s, r } = parseTracker(t); setSeconds(s); setPaused(!r); } else { setSeconds(0); setPaused(true); }
            }).catch(() => {});
        }
        prevMat.current = selectedMaterial;
    }, [selectedMaterial]);

    // Tick
    useEffect(() => {
        if (tick.current) clearInterval(tick.current);
        if (!paused && timerReady) tick.current = setInterval(() => setSeconds(p => p + 1), 1000);
        return () => { if (tick.current) clearInterval(tick.current); };
    }, [paused, timerReady]);

    // Files
    const fetchFiles = (initial = false) => { axiosCaller('GET', `project-blueprint/${product.productId}/products`).then(r => { setFiles(r?.data?.data || r?.data || []); if (initial) setFilesLoaded(true); }).catch(() => { if (initial) setFilesLoaded(true); }); };
    useEffect(() => { fetchFiles(true); }, [product.productId]);

    // Whether we're exiting status 3 (show resolution prompt)
    const isExitingLate = status.responsibilityStatusId === 3 && selectedStatusId !== null && selectedStatusId !== 3;
    const isMatExitingLate = selectedMaterial?.responsibilityStatusId === 3 && matStatusId !== null && matStatusId !== 3;

    // Handlers
    const submitProcess = async () => {
        if (!selectedStatusId) { toast.error(fm('production.statusForm.selectStatus', 'Zgjidhni statusin!')); return; }
        if (selectedStatusId === 3 && !lateComment.trim() && !adminWarning.trim()) { toast.error(fm('production.statusForm.lateCommentRequired', 'Komenti i vonesës duhet plotësuar!')); return; }
        if (selectedStatusId === 8 && !otherComment.trim()) { toast.error(fm('production.statusForm.otherReasonRequired', 'Shkruani arsyen!')); return; }
        setSaving(true);
        try {
            await axiosCaller('POST', 'responsibilities/clients/statuses', {
                productId: product.productId, responsibilityStatusId: selectedStatusId, responsibilityId,
                warning: lateComment, adminWarning, otherComment,
                returnCategoryId: selectedStatusId === 3 ? selectedCategoryId : undefined,
                resolutionNotes: isExitingLate ? resolutionNotes : undefined,
            });
            if (selectedStatusId === 1) { try { await axiosCaller('POST', 'clients/move-product-to-warehouse', { productId: product.productId, clientId: product.clientId, moduleId: 5 }); } catch {} }
            toast.success(fm('production.expansion.statusChanged', 'Statusi u ndryshua!')); onStatusUpdated(); onClose();
        } catch { toast.error(fm('production.expansion.cannotEdit', 'Nuk mund të editoni këtu!')); } finally { setSaving(false); }
    };

    const submitMaterial = async () => {
        if (!selectedMaterial || !matStatusId) return;
        if (matStatusId === 3 && !matLateComment.trim()) { toast.error(fm('production.statusForm.lateCommentRequired', 'Komenti i vonesës duhet plotësuar!')); return; }
        setSaving(true);
        try {
            const res = await axiosCaller('POST', 'responsibilities/clients/material/statuses', {
                productId: product.productId, responsibilityStatusId: matStatusId, responsibilityId,
                warning: matLateComment,
                materialId: selectedMaterial.temporaryMaterialId ? null : selectedMaterial.materialId,
                temporaryMaterialId: selectedMaterial.temporaryMaterialId || null,
                returnCategoryId: matStatusId === 3 ? matCategoryId : undefined,
                resolutionNotes: isMatExitingLate ? matResolutionNotes : undefined,
            });
            if (res?.data?.data === false) { toast.error(fm('production.expansion.cannotEdit', 'Nuk mund të editoni këtu!')); return; }
            toast.success(fm('production.expansion.statusChanged', 'Statusi u ndryshua!')); onStatusUpdated(); onClose();
        } catch { toast.error(fm('production.expansion.cannotEdit', 'Nuk mund të editoni këtu!')); } finally { setSaving(false); }
    };

    // Manual KuBIT consumption (manual mode) — consume one finished material's
    // raw-material stock in KuBIT. Backend returns ok:true (200) on success /
    // nothing-to-do, and a reason on 422 for benign cases (not linked, no doc).
    const handleKubitConsume = async (materialId: number) => {
        setConsumingMaterialId(materialId);
        try {
            const res: any = await axiosCaller('POST', 'responsibilities/clients/material/kubit-consume', {
                productId: product.productId, materialId,
            });
            const data = res?.data ?? {};
            if (data.ok && (data.fired ?? 0) > 0) {
                toast.success(fm('integrations.kubit.consume.toast.consumed', 'U konsumua në KuBIT'));
            } else {
                toast.info(fm('integrations.kubit.consume.toast.nothing', 'Asgjë për të konsumuar (tashmë e konsumuar)'));
            }
        } catch (err: any) {
            const reason = err?.response?.data?.reason;
            if (reason === 'material_not_linked') {
                toast.warning(fm('integrations.kubit.consume.toast.notLinked', 'Materiali nuk është i lidhur me KuBIT'));
            } else if (reason === 'no_manufacturing_document') {
                toast.info(fm('integrations.kubit.consume.toast.noDoc', 'S’ka dokument prodhimi në KuBIT'));
            } else {
                toast.error(fm('integrations.kubit.consume.toast.failed', 'Konsumimi në KuBIT dështoi'));
            }
        } finally {
            setConsumingMaterialId(null);
        }
    };

    const saveDates = async () => {
        if (!dateCom.trim()) { toast.error(fm('production.expansion.dateCommentRequired', 'Komenti duhet kur ndryshoni datat!')); return; }
        try { await axiosCaller('POST', 'admin/products/change-start-end-date', { productId: product.productId, start: editStart, end: editEnd, calendarComment: dateCom }); toast.success(fm('production.toast.datesChanged', 'Datat u ndryshuan!')); setDateCom(''); onStatusUpdated(); }
        catch { toast.error(fm('production.expansion.datesTaken', 'Këto data janë të zëna!')); }
    };

    const makePassive = async () => {
        try { await axiosCaller('POST', 'responsibilities/clients/statuses', { productId: product.productId, active: 0 }); toast.success(fm('production.expansion.madePassive', 'Produkti u bë pasiv!')); onStatusUpdated(); onClose(); } catch {}
    };

    const saveTempMat = async () => {
        if (!tempName.trim()) return;
        try {
            if (editTempId) {
                await axiosCaller('POST', `material-status/${editTempId}`, { productId: product.productId, responsibilityId, materialName: tempName });
            } else {
                await axiosCaller('POST', 'material-status', { productId: product.productId, responsibilityId, materialName: tempName });
            }
            setTempName(''); setEditTempId(null); setShowAddMat(false); onStatusUpdated();
        } catch {}
    };

    const saveInlineEdit = async () => {
        if (!inlineEditName.trim() || !inlineEditId) { setInlineEditId(null); return; }
        try {
            await axiosCaller('POST', `material-status/${inlineEditId}`, { productId: product.productId, responsibilityId, materialName: inlineEditName.trim() });
            setInlineEditId(null); setInlineEditName(''); onStatusUpdated();
        } catch {}
    };

    const deleteTempMat = async (id: number) => { try { await axiosCaller('DELETE', `material-status/${id}`); onStatusUpdated(); } catch {} };

    const uploadFiles = (fileList: File[]) => {
        const fd = new FormData();
        fileList.forEach(f => { fd.append('blueprint[]', f); fd.append('projectId', String(product.projectId)); fd.append('productId', String(product.productId)); });
        axiosCaller('POST', 'project-blueprint', fd).then(() => fetchFiles()).catch(() => toast.error(fm('production.expansion.uploadError', 'Gabim')));
    };

    const visibleStatuses = MAIN_STATUSES.filter(s => {
        if (s.adminOnly && !isAdmin) return false;
        if (s.id === 4 && hasMaterials && !isAdmin) return false;
        if (s.id === status.responsibilityStatusId) return false;
        return true;
    });

    const handleMaterialClick = (m: any) => {
        if (readOnly) return;
        const visible = isMaterialVisible(m.materialId, m.temporaryMaterialId, responsibilityId, product.statuses, isAdmin, flowMode);
        if (!visible) {
            toast.error(fm('production.expansion.materialLocked', 'Materiali në procesin paraprak nuk ka filluar ende punimet!'));
            return;
        }
        setSelectedMaterial(m);
        setMatStatusId(null);
        setMatLateComment('');
        setMatCategoryId(null);
        setMatResolutionNotes('');
        setMatCommentsReady(0);
    };

    const renderCategoryDropdown = (value: number | null, onChange: (v: number | null) => void) => (
        <select className="ep-category-select" value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}>
            <option value="">{fm('production.expansion.categoryOptional', 'Kategoria (opsionale)')}</option>
            {returnCategories.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
            ))}
        </select>
    );

    // Event banner renderer
    const renderEventBanner = (event: any, fallbackWarning?: string) => {
        if (event) {
            const isAdminEvent = event.eventType === 'admin_return';
            return (
                <div className={`ep-late-warning ${isAdminEvent ? 'ep-late-warning-admin' : 'ep-late-warning-worker'}`}>
                    <div className="ep-late-warning-header">
                        <span className={`ep-late-warning-dot ${isAdminEvent ? 'ep-dot-admin' : 'ep-dot-worker'}`} />
                        <span className="ep-late-warning-label">{isAdminEvent ? fm('production.history.returnedBy', 'Kthyer nga') : fm('production.history.blockedBy', 'Bllokuar nga')}: {event.returnedBy} — {event.returnedAt}</span>
                    </div>
                    {event.categoryName && <span className="ep-late-warning-category">{event.categoryName}</span>}
                    <p className="ep-late-warning-text">"{event.returnReason}"</p>
                </div>
            );
        }
        if (fallbackWarning) {
            return (
                <div className="ep-late-warning">
                    <span className="ep-late-warning-label"><FormattedMessage id="production.history.lateCommentLabel" defaultMessage="Komenti i vonesës" /></span>
                    <p className="ep-late-warning-text">{fallbackWarning}</p>
                </div>
            );
        }
        return null;
    };

    // Inline timeline renderer
    const renderTimeline = (events: any[], isOpen: boolean, setOpen: (v: boolean) => void) => {
        if (events.length === 0) return null;
        return (
            <div className="ep-timeline-section">
                <button className="ep-timeline-toggle" onClick={() => setOpen(!isOpen)} type="button">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                    <FormattedMessage
                        id="production.history.title"
                        defaultMessage="Historia ({count, plural, one {# ngjarje} other {# ngjarje}})"
                        values={{ count: events.length }}
                    />
                </button>
                {isOpen && (
                    <div className="hp-timeline">
                        {events.map((ev: any) => {
                            const isAdminEv = ev.eventType === 'admin_return';
                            return (
                                <div key={ev.id} className="hp-event">
                                    <div className="hp-event-header">
                                        <span className={`hp-event-dot ${isAdminEv ? 'hp-dot-admin' : 'hp-dot-worker'}`} />
                                        <span className="hp-event-date">{ev.returnedAt}</span>
                                        <span className="hp-event-type">{isAdminEv ? <FormattedMessage id="production.history.returnedBy" defaultMessage="Kthyer nga" /> : <FormattedMessage id="production.history.blockedBy" defaultMessage="Bllokuar nga" />}</span>
                                        <span className="hp-event-user">{ev.returnedBy}</span>
                                    </div>
                                    {ev.categoryName && <div className="hp-event-category">{ev.categoryName}</div>}
                                    <p className="hp-event-reason">"{ev.returnReason}"</p>
                                    {ev.resolvedAt ? (
                                        <div className="hp-event-resolved">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                            <span><FormattedMessage id="production.history.resolved" defaultMessage="Zgjidhur: {date}" values={{ date: ev.resolvedAt }} /></span>
                                            {ev.resolvedBy && <span className="hp-event-resolved-by"><FormattedMessage id="production.history.by" defaultMessage="nga {user}" values={{ user: ev.resolvedBy }} /></span>}
                                            {ev.resolutionSeconds != null && <span className="hp-event-duration">⏱ {formatDuration(ev.resolutionSeconds)}</span>}
                                        </div>
                                    ) : (
                                        <div className="hp-event-open">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                            <span><FormattedMessage id="production.history.stillOpen" defaultMessage="Ende pa zgjidhur" /></span>
                                        </div>
                                    )}
                                    {ev.resolutionNotes && <p className="hp-event-resolution-notes"><FormattedMessage id="production.history.resolution" defaultMessage='Zgjidhja: "{notes}"' values={{ notes: ev.resolutionNotes }} /></p>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // ═══ Material drill-in ═══
    if (selectedMaterial) {
        const ms = STATUS_MAP[selectedMaterial.responsibilityStatusId] || STATUS_MAP[9];
        const matStatuses = [
            { id: 1, label: fm('production.status.completed', 'Përfunduar'), icon: '✓', key: 'completed' },
            { id: 3, label: fm('production.status.late', 'Me vonesë'), icon: '⚠', key: 'late' },
            { id: 4, label: fm('production.status.inProcess', 'Në proces'), icon: '▶', key: 'inProcess' },
            ...(isAdmin ? [{ id: 2, label: fm('production.status.toDo', 'Për tu realizuar'), icon: '↺', key: 'toDo' }] : []),
        ].filter(s => s.id !== selectedMaterial.responsibilityStatusId);

        const selectedMatInfo = matStatusId ? (STATUS_MAP[matStatusId] || STATUS_MAP[9]) : null;

        return (
            <div className="ep-overlay" onClick={onClose}>
                <div className="ep-modal ep-wide" onClick={e => e.stopPropagation()}>
                    {!matAllReady && (
                        <div className="ep-loader">
                            <div className="ep-loader-pulse">
                                <div className="ep-loader-dot" style={{ background: ms.color }} />
                                <div className="ep-loader-dot" style={{ background: ms.color }} />
                                <div className="ep-loader-dot" style={{ background: ms.color }} />
                            </div>
                        </div>
                    )}

                    <div className={`ep-content ${matAllReady ? 'ep-content-ready' : 'ep-content-hidden'}`}>
                        <div className="ep-topbar">
                            <button className="ep-back" onClick={() => { setSelectedMaterial(null); setMatStatusId(null); setMatLateComment(''); setMatCategoryId(null); setMatResolutionNotes(''); }} type="button">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                                <FormattedMessage id="production.expansion.back" defaultMessage="Mbrapa" />
                            </button>
                            <div className="ep-topbar-center">
                                <span className="ep-status-badge" style={{ background: ms.bg, color: ms.color, borderColor: ms.color }}>{fm(`production.status.${ms.key}`, ms.label)}</span>
                                {timerReady && !paused && <span className="ep-timer-badge" style={{ color: ms.color }}>{formatTime(seconds)}</span>}
                                {timerReady && paused && seconds > 0 && <span className="ep-timer-badge ep-timer-paused">{formatTime(seconds)}</span>}
                            </div>
                            <button className="ep-close" onClick={onClose} type="button">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <div className="ep-header">
                            <h3 className="ep-title">{selectedMaterial.materialName || selectedMaterial.temporaryMaterial || fm('production.material.default', 'Material')}</h3>
                            <p className="ep-breadcrumb">
                                <span>{product.productName}</span>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                <span>{responsibilityName}</span>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                <span className="ep-breadcrumb-current">{selectedMaterial.materialName || selectedMaterial.temporaryMaterial || fm('production.material.default', 'Material')}</span>
                            </p>
                            {(selectedMaterial.userName || selectedMaterial.updatedAt) && (
                                <p className="ep-last-update">
                                    {selectedMaterial.updatedAt
                                        ? <FormattedMessage id="production.expansion.updatedByOn" defaultMessage="Përditësuar nga {user} më {date}" values={{ user: <strong>{selectedMaterial.userName || fm('production.expansion.systemUser', 'sistemi')}</strong>, date: selectedMaterial.updatedAt }} />
                                        : <FormattedMessage id="production.expansion.updatedBy" defaultMessage="Përditësuar nga {user}" values={{ user: <strong>{selectedMaterial.userName || fm('production.expansion.systemUser', 'sistemi')}</strong> }} />}
                                </p>
                            )}
                        </div>

                        <div className="ep-body">
                            {/* Banner: show latest event or warning when material is late */}
                            {selectedMaterial.responsibilityStatusId === 3 && renderEventBanner(materialEvents[0], selectedMaterial.warning)}

                            {/* Status cards */}
                            <div className="ep-zone ep-zone-action">
                                <span className="ep-zone-title"><FormattedMessage id="production.expansion.changeStatus" defaultMessage="Ndrysho statusin" /></span>
                                <div className="ep-status-grid">
                                    {matStatuses.map(s => {
                                        const si = STATUS_MAP[s.id] || STATUS_MAP[9];
                                        const isActive = matStatusId === s.id;
                                        return (
                                            <button key={s.id} className={`ep-status-card ${isActive ? 'active' : ''}`} onClick={() => setMatStatusId(s.id)} type="button"
                                                style={{ borderColor: si.color, background: si.bg, color: si.color }}>
                                                <span className="ep-sc-icon" style={{ color: si.color }}>{s.icon}</span>
                                                <span className="ep-sc-label" style={{ color: si.color }}>{s.label}</span>
                                            </button>
                                        );
                                    })}
                                    {(() => {
                                        const isPauseActive = [5,6,7,8].includes(matStatusId || 0);
                                        return (
                                            <button className={`ep-status-card ${isPauseActive ? 'active' : ''}`}
                                                onClick={() => setShowPauseOptions(!showPauseOptions)} type="button"
                                                style={{ borderColor: '#f97316', background: '#FCFCE5', color: '#f97316' }}>
                                                <span className="ep-sc-icon" style={{ color: '#f97316' }}>⏸</span>
                                                <span className="ep-sc-label" style={{ color: '#f97316' }}>{fm('production.statusForm.pause', 'Pauzo')}</span>
                                            </button>
                                        );
                                    })()}
                                </div>
                                {showPauseOptions && (
                                    <div className="ep-pause-list">
                                        {PAUSE_STATUSES.map(s => (
                                            <button key={s.id} className={`ep-pause-opt ${matStatusId === s.id ? 'active' : ''}`} onClick={() => setMatStatusId(s.id)} type="button">{s.label}</button>
                                        ))}
                                    </div>
                                )}

                                {/* Vonesë form: category + comment (material) */}
                                {matStatusId === 3 && (
                                    <>
                                        {renderCategoryDropdown(matCategoryId, setMatCategoryId)}
                                        <textarea className="ep-textarea ep-textarea-late" value={matLateComment} onChange={e => setMatLateComment(e.target.value)} placeholder={fm('production.expansion.latePlaceholder', 'Komenti i vonesës (i detyrueshëm)...')} rows={2} />
                                    </>
                                )}

                                {/* Resolution prompt: when exiting status 3 (material) */}
                                {isMatExitingLate && (
                                    <textarea className="ep-textarea ep-textarea-resolve" value={matResolutionNotes} onChange={e => setMatResolutionNotes(e.target.value)} placeholder={fm('production.expansion.resolutionPlaceholder', 'Si u zgjidh problemi? (opsionale)')} rows={2} />
                                )}

                                <button className="ep-save-btn" onClick={submitMaterial} disabled={saving || !matStatusId} type="button"
                                    style={selectedMatInfo ? { background: selectedMatInfo.color } : undefined}>
                                    {saving
                                        ? <FormattedMessage id="production.statusForm.saving" defaultMessage="Duke ruajtur..." />
                                        : <FormattedMessage id="production.expansion.changeStatus" defaultMessage="Ndrysho statusin" />}
                                </button>
                            </div>

                            <div className="ep-divider" />

                            {/* Inline event timeline — prioritized above comments/files */}
                            {renderTimeline(materialEvents, matTimelineOpen, setMatTimelineOpen)}

                            {isAdmin ? (
                                <>
                                    {/* Admin: Comments (2-col) then Files — original layout */}
                                    <div className="ep-comments-row">
                                        <div className="ep-comment-col">
                                            <CommentThread productId={product.productId} responsibilityId={responsibilityId} materialId={selectedMaterial.materialId}
                                                type="comment" label={fm('production.expansion.comments', 'Komentet')} placeholder={fm('production.detail.commentPlaceholder', 'Shkruaj një koment...')} onLoaded={onMatCommentLoaded} onCommentPosted={onStatusUpdated} />
                                        </div>
                                        <div className="ep-comment-col">
                                            <CommentThread productId={product.productId} responsibilityId={responsibilityId} materialId={selectedMaterial.materialId}
                                                type="admin_warning" label={fm('production.expansion.adminLabel', 'Admin')} placeholder={fm('production.statusForm.adminWarningPlaceholder', 'Paralajmërim për punëtorin...')} onLoaded={onMatCommentLoaded} onCommentPosted={onStatusUpdated} />
                                        </div>
                                    </div>
                                    <div className="ep-divider" />
                                    <div className="ep-zone">
                                        <span className="ep-zone-title"><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></span>
                                        <MikaFileManager
                                            files={files.map((f: any) => ({ id: f.id, name: f.nameOfBlueprint || fm('production.material.fileDefault', 'Skedar'), url: f.blueprint } as MikaFile))}
                                            onDelete={(id) => { axiosCaller('DELETE', `project-blueprint/${id}`).then(() => fetchFiles()).catch(() => {}); }}
                                            toolbarUpload={{ accept: '*', multiple: true, onUpload: uploadFiles }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Simple user: Comments + Files side by side */}
                                    <div className="ep-bottom-row">
                                        <div className="ep-bottom-col">
                                            <CommentThread productId={product.productId} responsibilityId={responsibilityId} materialId={selectedMaterial.materialId}
                                                type="comment" label={fm('production.expansion.comments', 'Komentet')} placeholder={fm('production.detail.commentPlaceholder', 'Shkruaj një koment...')} onLoaded={onMatCommentLoaded} onCommentPosted={onStatusUpdated} />
                                        </div>
                                        <div className="ep-bottom-col">
                                            <span className="ep-zone-title"><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></span>
                                            <MikaFileManager
                                                files={files.map((f: any) => ({ id: f.id, name: f.nameOfBlueprint || fm('production.material.fileDefault', 'Skedar'), url: f.blueprint } as MikaFile))}
                                                toolbarUpload={{ accept: '*', multiple: true, onUpload: uploadFiles }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {confirmDeleteTemp && (
                        <div className="delete-confirm-overlay">
                            <div className="delete-confirm-box">
                                <p className="delete-confirm-title"><FormattedMessage id="production.expansion.confirmTitle" defaultMessage="Jeni të sigurt?" /></p>
                                <p className="delete-confirm-msg"><FormattedMessage id="production.expansion.confirmDeleteMaterial" defaultMessage='Dëshironi të fshini materialin e përkohshëm "{name}"?' values={{ name: confirmDeleteTemp.name }} /></p>
                                <div className="delete-confirm-actions">
                                    <button type="button" className="confirm-btn cancel" onClick={() => setConfirmDeleteTemp(null)}><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                                    <button type="button" className="confirm-btn confirm" onClick={() => { deleteTempMat(confirmDeleteTemp.id); setConfirmDeleteTemp(null); }}><FormattedMessage id="production.expansion.deleteConfirmShort" defaultMessage="Fshij" /></button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ═══ Main modal — process status ═══
    const selectedInfo = selectedStatusId ? (STATUS_MAP[selectedStatusId] || STATUS_MAP[9]) : null;

    return (
        <div className="ep-overlay" onClick={onClose}>
            <div className="ep-modal ep-wide" onClick={e => e.stopPropagation()}>
                {!allReady && (
                    <div className="ep-loader">
                        <div className="ep-loader-pulse">
                            <div className="ep-loader-dot" style={{ background: currentStatusInfo.color }} />
                            <div className="ep-loader-dot" style={{ background: currentStatusInfo.color }} />
                            <div className="ep-loader-dot" style={{ background: currentStatusInfo.color }} />
                        </div>
                    </div>
                )}

                <div className={`ep-content ${allReady ? 'ep-content-ready' : 'ep-content-hidden'}`}>
                    <div className="ep-topbar">
                        <div className="ep-topbar-center">
                            <span className="ep-status-badge" style={{ background: currentStatusInfo.bg, color: currentStatusInfo.color, borderColor: currentStatusInfo.color }}>{fm(`production.status.${currentStatusInfo.key}`, currentStatusInfo.label)}</span>
                            {timerReady && !paused && <span className="ep-timer-badge" style={{ color: currentStatusInfo.color }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                {formatTime(seconds)}
                            </span>}
                            {timerReady && paused && seconds > 0 && <span className="ep-timer-badge ep-timer-paused">{formatTime(seconds)}</span>}
                        </div>
                        <div className="ep-topbar-right">
                            {isAdmin && <button className={`ep-topbar-btn ${showInfo ? 'active' : ''}`} onClick={() => setShowInfo(!showInfo)} type="button" title={fm('production.expansion.infoAdmin', 'Info admin')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                            </button>}
                            <button className="ep-close" onClick={onClose} type="button">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                    </div>

                    <div className="ep-header">
                        <h3 className="ep-title">{responsibilityName}</h3>
                        <p className="ep-breadcrumb">
                            <span>{product.clientName}</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                            <span>{product.productName}</span>
                        </p>
                        {(status.userName || status.updatedAt) && (
                            <p className="ep-last-update">
                                {status.updatedAt
                                    ? <FormattedMessage id="production.expansion.updatedByOn" defaultMessage="Përditësuar nga {user} më {date}" values={{ user: <strong>{status.userName || fm('production.expansion.systemUser', 'sistemi')}</strong>, date: status.updatedAt }} />
                                    : <FormattedMessage id="production.expansion.updatedBy" defaultMessage="Përditësuar nga {user}" values={{ user: <strong>{status.userName || fm('production.expansion.systemUser', 'sistemi')}</strong> }} />}
                            </p>
                        )}
                    </div>

                    {/* Banner: show latest event or warning when process is late */}
                    {status.responsibilityStatusId === 3 && renderEventBanner(processEvents[0], status.warning)}

                    {/* Admin info */}
                    {isAdmin && showInfo && (
                        <div className="ep-info-bar">
                            <div className="ep-info-grid">
                                <div><span className="ep-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>{product.clientName}</div>
                                <div><span className="ep-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>{product.projectName}</div>
                                <div><span className="ep-info-label"><FormattedMessage id="production.board.productCol" defaultMessage="Produkti" /></span>{product.productName}</div>
                                <div><span className="ep-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>{product.quantity} {product.unit}</div>
                            </div>
                            {!readOnly && (
                                <div className="ep-info-dates">
                                    <input type="date" value={editStart} onChange={e => setEditStart(e.target.value)} />
                                    <input type="date" value={editEnd} onChange={e => setEditEnd(e.target.value)} />
                                    <input type="text" value={dateCom} onChange={e => setDateCom(e.target.value)} placeholder={fm('production.expansion.reasonPlaceholder', 'Arsyeja...')} className="ep-info-comment" />
                                    <button className="ep-info-save" onClick={saveDates} type="button"><FormattedMessage id="production.detail.saveDates" defaultMessage="Ruaj datat" /></button>
                                    <button className="ep-info-passive" onClick={makePassive} type="button"><FormattedMessage id="production.expansion.makePassive" defaultMessage="Bëje pasiv" /></button>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="ep-body">
                        {/* Status cards — hidden for simple users when process has materials, and in readOnly */}
                        {!readOnly && (isAdmin || !hasMaterials) && (
                            <>
                            <div className="ep-zone ep-zone-action">
                                <span className="ep-zone-title"><FormattedMessage id="production.expansion.changeStatus" defaultMessage="Ndrysho statusin" /></span>
                                <div className="ep-status-grid">
                                    {visibleStatuses.map(s => {
                                        const si = STATUS_MAP[s.id] || STATUS_MAP[9];
                                        const isActive = selectedStatusId === s.id;
                                        return (
                                            <button key={s.id} className={`ep-status-card ${isActive ? 'active' : ''}`} onClick={() => { setSelectedStatusId(s.id); setShowPauseOptions(false); }} type="button"
                                                style={{ borderColor: si.color, background: si.bg, color: si.color }}>
                                                <span className="ep-sc-icon" style={{ color: si.color }}>{s.icon}</span>
                                                <span className="ep-sc-label" style={{ color: si.color }}>{s.label}</span>
                                            </button>
                                        );
                                    })}
                                    {(() => {
                                        const isPauseActive = showPauseOptions || [5,6,7,8].includes(selectedStatusId || 0);
                                        return (
                                            <button className={`ep-status-card ${isPauseActive ? 'active' : ''}`}
                                                onClick={() => setShowPauseOptions(!showPauseOptions)} type="button"
                                                style={{ borderColor: '#f97316', background: '#FCFCE5', color: '#f97316' }}>
                                                <span className="ep-sc-icon" style={{ color: '#f97316' }}>⏸</span>
                                                <span className="ep-sc-label" style={{ color: '#f97316' }}>{fm('production.statusForm.pause', 'Pauzo')}</span>
                                            </button>
                                        );
                                    })()}
                                </div>
                                {showPauseOptions && (
                                    <div className="ep-pause-list">
                                        {PAUSE_STATUSES.map(s => (
                                            <button key={s.id} className={`ep-pause-opt ${selectedStatusId === s.id ? 'active' : ''}`} onClick={() => setSelectedStatusId(s.id)} type="button">{s.label}</button>
                                        ))}
                                    </div>
                                )}

                                {/* Vonesë form: category + comment (process) */}
                                {selectedStatusId === 3 && (
                                    <>
                                        {renderCategoryDropdown(selectedCategoryId, setSelectedCategoryId)}
                                        <textarea className="ep-textarea ep-textarea-late" value={lateComment} onChange={e => setLateComment(e.target.value)} placeholder={fm('production.expansion.latePlaceholder', 'Komenti i vonesës (i detyrueshëm)...')} rows={2} />
                                    </>
                                )}

                                {selectedStatusId === 8 && (
                                    <textarea className="ep-textarea" value={otherComment} onChange={e => setOtherComment(e.target.value)} placeholder={fm('production.statusForm.writeReason', 'Shkruaj arsyen...')} rows={2} />
                                )}

                                {isAdmin && selectedStatusId === 3 && (
                                    <textarea className="ep-textarea" value={adminWarning} onChange={e => setAdminWarning(e.target.value)} placeholder={fm('production.expansion.adminWarningOptional', 'Paralajmërim admin (opsional)...')} rows={2} />
                                )}

                                {/* Resolution prompt: when exiting status 3 (process) */}
                                {isExitingLate && (
                                    <textarea className="ep-textarea ep-textarea-resolve" value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} placeholder={fm('production.expansion.resolutionPlaceholder', 'Si u zgjidh problemi? (opsionale)')} rows={2} />
                                )}

                                <button className="ep-save-btn" onClick={submitProcess} disabled={saving || !selectedStatusId} type="button"
                                    style={selectedInfo ? { background: selectedInfo.color } : undefined}>
                                    {saving
                                        ? <FormattedMessage id="production.statusForm.saving" defaultMessage="Duke ruajtur..." />
                                        : <FormattedMessage id="production.expansion.changeStatus" defaultMessage="Ndrysho statusin" />}
                                </button>
                            </div>

                            <div className="ep-divider" />
                            </>
                        )}

                        {isAdmin ? (
                            <>
                                {/* Admin: Comments (2-col), then Materials + Files (2-col), then Timeline */}
                                <div className="ep-comments-row">
                                    <div className="ep-comment-col">
                                        <CommentThread productId={product.productId} responsibilityId={responsibilityId}
                                            type="comment" label={fm('production.expansion.comments', 'Komentet')} placeholder={fm('production.detail.commentPlaceholder', 'Shkruaj një koment...')} onLoaded={onCommentLoaded} onCommentPosted={onStatusUpdated} readOnly={readOnly} />
                                    </div>
                                    <div className="ep-comment-col">
                                        <CommentThread productId={product.productId} responsibilityId={responsibilityId}
                                            type="admin_warning" label={fm('production.expansion.adminLabel', 'Admin')} placeholder={fm('production.statusForm.adminWarningPlaceholder', 'Paralajmërim për punëtorin...')} onLoaded={onCommentLoaded} onCommentPosted={onStatusUpdated} readOnly={readOnly} />
                                    </div>
                                </div>

                                <div className="ep-divider" />

                                <div className="ep-bottom-row">
                                    <div className="ep-bottom-col">
                                        <div className="ep-zone-head">
                                            <span className="ep-zone-title"><FormattedMessage id="production.expansion.materials" defaultMessage="Materialet" /></span>
                                            {hasMaterials && <span className="ep-zone-badge">{status.completedMaterials}/{status.materialCount}</span>}
                                        </div>
                                        {hasMaterials && (
                                            <div className="ep-mat-list">
                                                {materialStatuses.map((m: any) => {
                                                    const msi = STATUS_MAP[m.responsibilityStatusId] || STATUS_MAP[9];
                                                    const visible = isMaterialVisible(m.materialId, m.temporaryMaterialId, responsibilityId, product.statuses, isAdmin, flowMode);
                                                    return (
                                                        <div key={m.id || m.materialId} className={`ep-mat-row ${!visible ? 'ep-mat-locked' : ''}`}
                                                            title={!visible ? fm('production.expansion.materialLockedTooltip', 'Materiali në procesin paraprak nuk ka filluar ende punimet') : ''}>
                                                            <span className="ep-mat-dot" style={{ background: visible ? msi.color : '#cbd5e1' }} onClick={() => handleMaterialClick(m)} />
                                                            {inlineEditId === m.id ? (
                                                                <span className="ep-mat-inline-wrap">
                                                                    <input
                                                                        className="ep-mat-inline-edit"
                                                                        type="text"
                                                                        value={inlineEditName}
                                                                        onChange={e => setInlineEditName(e.target.value)}
                                                                        onKeyDown={e => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') setInlineEditId(null); }}
                                                                        onClick={e => e.stopPropagation()}
                                                                        autoFocus
                                                                    />
                                                                    <button type="button" className="ep-mat-inline-btn ep-mat-inline-save" title={fm('common.action.save', 'Ruaj')} onClick={(e) => { e.stopPropagation(); saveInlineEdit(); }}>
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                                                    </button>
                                                                    <button type="button" className="ep-mat-inline-btn ep-mat-inline-cancel" title={fm('common.action.cancel', 'Anulo')} onClick={(e) => { e.stopPropagation(); setInlineEditId(null); }}>
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                                                    </button>
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span className={`ep-mat-name ${!visible ? 'ep-mat-name-locked' : ''}`} onClick={() => handleMaterialClick(m)}>{m.materialName || m.temporaryMaterial || fm('production.material.default', 'Material')}</span>
                                                                    {m.temporaryMaterialId && visible && !readOnly && (
                                                                        <span className="ep-mat-temp-actions">
                                                                            <button type="button" className="ep-mat-temp-btn ep-mat-temp-edit" title={fm('common.action.edit', 'Edito')} onClick={(e) => { e.stopPropagation(); setInlineEditId(m.id); setInlineEditName(m.materialName || ''); }}>
                                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                                            </button>
                                                                            <button type="button" className="ep-mat-temp-btn ep-mat-temp-delete" title={fm('common.action.delete', 'Fshi')} onClick={(e) => { e.stopPropagation(); setConfirmDeleteTemp({ id: m.id, name: m.materialName || fm('production.material.default', 'Material') }); }}>
                                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                            {visible ? (
                                                                <>
                                                                    <span className="ep-mat-status" style={{ color: msi.color }}>{fm(`production.status.${msi.key}`, msi.label)}</span>
                                                                    {kubitEnabled && !kubitAutoConsume && !readOnly && !m.temporaryMaterialId && m.responsibilityStatusId === 1 && (
                                                                        <button
                                                                            type="button"
                                                                            className="ep-mat-kubit-btn"
                                                                            disabled={consumingMaterialId === m.materialId}
                                                                            title={fm('integrations.kubit.consume.button', 'Konsumo në KuBIT')}
                                                                            onClick={(e) => { e.stopPropagation(); handleKubitConsume(m.materialId); }}
                                                                        >
                                                                            {consumingMaterialId === m.materialId
                                                                                ? fm('integrations.kubit.consume.consuming', 'Duke konsumuar…')
                                                                                : fm('integrations.kubit.consume.button', 'Konsumo në KuBIT')}
                                                                        </button>
                                                                    )}
                                                                    <svg className="ep-mat-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" onClick={() => handleMaterialClick(m)}><polyline points="9 18 15 12 9 6" /></svg>
                                                                </>
                                                            ) : (
                                                                <svg className="ep-mat-lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {!readOnly && (showAddMat ? (
                                            <div className="ep-add-mat-form">
                                                <input type="text" value={tempName} onChange={e => setTempName(e.target.value)} placeholder={fm('production.expansion.materialNamePlaceholder', 'Emri i materialit...')} onKeyDown={e => { if (e.key === 'Enter') saveTempMat(); if (e.key === 'Escape') { setShowAddMat(false); setTempName(''); } }} autoFocus />
                                                <button onClick={saveTempMat} type="button"><FormattedMessage id="common.action.save" defaultMessage="Ruaj" /></button>
                                                <button onClick={() => { setShowAddMat(false); setTempName(''); }} type="button"><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                                            </div>
                                        ) : (
                                            <button className="ep-add-mat-btn" onClick={() => setShowAddMat(true)} type="button"><FormattedMessage id="production.expansion.addMaterial" defaultMessage="+ Shto material" /></button>
                                        ))}
                                    </div>
                                    <div className="ep-bottom-col">
                                        <span className="ep-zone-title"><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></span>
                                        <MikaFileManager
                                            files={files.map((f: any) => ({ id: f.id, name: f.nameOfBlueprint || fm('production.material.fileDefault', 'Skedar'), url: f.blueprint } as MikaFile))}
                                            onDelete={readOnly ? undefined : (id) => { axiosCaller('DELETE', `project-blueprint/${id}`).then(() => fetchFiles()).catch(() => {}); }}
                                            toolbarUpload={readOnly ? undefined : { accept: '*', multiple: true, onUpload: uploadFiles }}
                                        />
                                    </div>
                                </div>

                                {renderTimeline(processEvents, timelineOpen, setTimelineOpen)}
                            </>
                        ) : (
                            <>
                                {/* Simple user: Materials (full width), Timeline, then Comments + Files (2-col) */}
                                <div className="ep-zone">
                                    <div className="ep-zone-head">
                                        <span className="ep-zone-title"><FormattedMessage id="production.expansion.materials" defaultMessage="Materialet" /></span>
                                        {hasMaterials && <span className="ep-zone-badge">{status.completedMaterials}/{status.materialCount}</span>}
                                    </div>
                                    {hasMaterials && (
                                        <div className="ep-mat-list">
                                            {materialStatuses.map((m: any) => {
                                                const msi = STATUS_MAP[m.responsibilityStatusId] || STATUS_MAP[9];
                                                const visible = isMaterialVisible(m.materialId, m.temporaryMaterialId, responsibilityId, product.statuses, isAdmin, flowMode);
                                                return (
                                                    <div key={m.id || m.materialId} className={`ep-mat-row ${!visible ? 'ep-mat-locked' : ''}`}
                                                        onClick={() => handleMaterialClick(m)}
                                                        title={!visible ? fm('production.expansion.materialLockedTooltip', 'Materiali në procesin paraprak nuk ka filluar ende punimet') : ''}>
                                                        <span className="ep-mat-dot" style={{ background: visible ? msi.color : '#cbd5e1' }} />
                                                        <span className={`ep-mat-name ${!visible ? 'ep-mat-name-locked' : ''}`}>{m.materialName || m.temporaryMaterial || fm('production.material.default', 'Material')}</span>
                                                        {visible ? (
                                                            <>
                                                                <span className="ep-mat-status" style={{ color: msi.color }}>{fm(`production.status.${msi.key}`, msi.label)}</span>
                                                                <svg className="ep-mat-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                                            </>
                                                        ) : (
                                                            <svg className="ep-mat-lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {!readOnly && (showAddMat ? (
                                        <div className="ep-add-mat-form">
                                            <input type="text" value={tempName} onChange={e => setTempName(e.target.value)} placeholder={fm('production.expansion.materialNamePlaceholder', 'Emri i materialit...')} onKeyDown={e => { if (e.key === 'Enter') saveTempMat(); if (e.key === 'Escape') { setShowAddMat(false); setTempName(''); } }} autoFocus />
                                            <button onClick={saveTempMat} type="button"><FormattedMessage id="common.action.save" defaultMessage="Ruaj" /></button>
                                            <button onClick={() => { setShowAddMat(false); setTempName(''); }} type="button"><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                                        </div>
                                    ) : (
                                        <button className="ep-add-mat-btn" onClick={() => setShowAddMat(true)} type="button"><FormattedMessage id="production.expansion.addMaterial" defaultMessage="+ Shto material" /></button>
                                    ))}
                                </div>

                                {renderTimeline(processEvents, timelineOpen, setTimelineOpen)}

                                <div className="ep-divider" />
                                <div className="ep-bottom-row">
                                    <div className="ep-bottom-col">
                                        <CommentThread productId={product.productId} responsibilityId={responsibilityId}
                                            type="comment" label={fm('production.expansion.comments', 'Komentet')} placeholder={fm('production.detail.commentPlaceholder', 'Shkruaj një koment...')} onLoaded={onCommentLoaded} onCommentPosted={onStatusUpdated} readOnly={readOnly} />
                                    </div>
                                    <div className="ep-bottom-col">
                                        <span className="ep-zone-title"><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></span>
                                        <MikaFileManager
                                            files={files.map((f: any) => ({ id: f.id, name: f.nameOfBlueprint || fm('production.material.fileDefault', 'Skedar'), url: f.blueprint } as MikaFile))}
                                            toolbarUpload={readOnly ? undefined : { accept: '*', multiple: true, onUpload: uploadFiles }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    {confirmDeleteTemp && (
                        <div className="delete-confirm-overlay">
                            <div className="delete-confirm-box">
                                <p className="delete-confirm-title"><FormattedMessage id="production.expansion.confirmTitle" defaultMessage="Jeni të sigurt?" /></p>
                                <p className="delete-confirm-msg"><FormattedMessage id="production.expansion.confirmDeleteMaterial" defaultMessage='Dëshironi të fshini materialin e përkohshëm "{name}"?' values={{ name: confirmDeleteTemp.name }} /></p>
                                <div className="delete-confirm-actions">
                                    <button type="button" className="confirm-btn cancel" onClick={() => setConfirmDeleteTemp(null)}><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                                    <button type="button" className="confirm-btn confirm" onClick={() => { deleteTempMat(confirmDeleteTemp.id); setConfirmDeleteTemp(null); }}><FormattedMessage id="production.expansion.deleteConfirmShort" defaultMessage="Fshij" /></button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExpansionPanel;
